import React, { useMemo } from 'react';
import { Box, Typography, Stack, useTheme } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { categoricalColors, formatMoneyFull } from './chartConfig';
import { useAuth } from '../../../context/AuthContext';

export interface DonutSlice {
  key: string;
  label: string;
  amount: number;
  count: number;
  color?: string;
}

interface BreakdownDonutProps {
  slices: DonutSlice[];
  height?: number;
  totalLabel?: string;
  formatTotal?: (n: number) => string;
}

/**
 * Donut chart + inline legend with labels, amounts, and a colour swatch.
 * Recharts' built-in Legend is intentionally not used — the side-by-side
 * layout reads better and stays elegant at any width.
 */
const BreakdownDonut: React.FC<BreakdownDonutProps> = ({
  slices,
  height = 260,
  totalLabel = 'Total',
  formatTotal,
}) => {
  const theme = useTheme();
  const { currency } = useAuth();
  const colors = categoricalColors(theme);

  const enriched = useMemo(
    () =>
      slices.map((s, i) => ({
        ...s,
        color: s.color || colors[i % colors.length],
      })),
    [slices, colors]
  );

  const total = useMemo(() => enriched.reduce((sum, s) => sum + (s.amount || 0), 0), [enriched]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height, width: '100%' }}>
      <Box sx={{ position: 'relative', width: 200, height, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={enriched}
              dataKey="amount"
              nameKey="label"
              innerRadius={62}
              outerRadius={95}
              paddingAngle={2}
              stroke="none"
            >
              {enriched.map((s, i) => (
                <Cell key={i} fill={s.color} />
              ))}
            </Pie>
            <Tooltip
              // Rechart tooltips render inside the chart container with a low
              // implicit z-index. Push it above sibling cards so a slice
              // hover never gets clipped by the neighbouring donut/chart.
              wrapperStyle={{ zIndex: 1200, pointerEvents: 'none' }}
              contentStyle={{
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
                fontSize: 12,
                // Dark mode only — light mode uses recharts' default ink.
                ...(theme.palette.mode === 'dark' && { color: theme.palette.text.primary }),
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 6px 20px rgba(0,0,0,0.5)'
                  : '0 4px 16px rgba(0,0,0,0.08)',
              }}
              itemStyle={theme.palette.mode === 'dark' ? { color: theme.palette.text.primary } : undefined}
              formatter={(value: number, _n: string, ctx: any) => [
                formatMoneyFull(value, currency),
                ctx.payload.label,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5} textTransform="uppercase">
            {totalLabel}
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {formatTotal ? formatTotal(total) : formatMoneyFull(total, currency)}
          </Typography>
        </Box>
      </Box>

      <Stack spacing={1} sx={{ flex: 1, overflow: 'auto', maxHeight: height, pr: 1 }}>
        {enriched.length === 0 ? (
          <Typography variant="body2" color="text.disabled">No data</Typography>
        ) : (
          enriched.map(s => {
            const pct = total > 0 ? ((s.amount / total) * 100).toFixed(1) : '0.0';
            return (
              <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatMoneyFull(s.amount, currency)} · {pct}%
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Stack>
    </Box>
  );
};

export default BreakdownDonut;
